import React from 'react';

import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import { Grid } from '@mui/material';

import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import { IAPIKey } from '../../utilities/schemas/apiKey';
import resourceNames from '../../constants/resourceNames';
import AuditExtraInfo from '../audit/AuditExtraInfo';

const ApiKeyAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IAPIKey>>();
  const { data, isLoading } = useGetOne<AuditTrack<IAPIKey>>(`audit-tracks/${record?.id}/previous`, { id: '' });

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    id: resourceData.id?.hasChanged,
    clientId: resourceData.clientId?.hasChanged,
    permissionRoleIds: resourceData.permissionRoleIds?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.clientId && <TextDiffField source="clientId" label="Client ID" />}

          {hasFieldChanges.permissionRoleIds && (
            <ReferenceArrayDiffField
              source="permissionRoleIds"
              label="Permission roles"
              reference={resourceNames.PERMISSION_ROLES}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}

          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" showTime />}
        </RecordContextProvider>
      )}
    </Grid>
  );
};

export default ApiKeyAuditDetails;
